<template>
  <span
    ref="icon"
    :class="['sf-icon', iconColorClass, iconSizeClass]"
    :style="iconCustomStyle"
    v-bind="$attrs"
  >
    <slot v-bind="{ viewBox, iconPaths, icon }">
      <svg
        class="sf-icon-path"
        :viewBox="iconViewBox"
        preserveAspectRatio="none"
      >
        <defs v-if="coverageNumber !== 1">
          <linearGradient
            :id="`linearGradient-${gradientUid}`"
            x1="0"
            y1="0"
            x2="1"
            y2="0"
          >
            <stop
              :offset="coverageNumber"
              stop-color="var(--icon-color)"
            />
            <stop
              offset="0"
              stop-color="var(--icon-color-negative, var(--c-gray-variant))"
            />
          </linearGradient>
        </defs>
        <path
          v-for="(path, index) in iconPaths"
          :key="index"
          :d="path"
          :fill="fillPath"
          style="height: 100%;"
        />
      </svg>
    </slot>
  </span>
</template>
<script>
import { useId } from 'vue';
import { icons as sfIcons } from '~/variables/icons';
import { iconColorsValues as SF_COLORS } from '~/variables/colors';
import { sizesValues as SF_SIZES } from '~/variables/sizes';

export default {
  name: 'SfIcon',
  props: {
    icon: {
      type: [String, Array],
      default: '',
    },
    size: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    viewBox: {
      type: String,
      default: '0 0 24 24',
    },
    coverage: {
      type: [String, Number],
      default: 1,
    },
  },
  setup() {

    // Stable across SSR + client hydration.
    const gradientUid = useId();

    return {
      gradientUid,
    };
  
  },
  computed: {
    coverageNumber() {

      const value = typeof this.coverage === 'string'
        ? Number(this.coverage)
        : this.coverage;

      return Number.isFinite(value) ? value : 1;

    },
    isSFColors() {

      return SF_COLORS.includes(this.color.trim());
    
    },
    isSFSizes() {

      const size = this.size.trim();

      return SF_SIZES.includes(size);
    
    },
    iconColorClass() {

      return this.isSFColors ? `color-${this.color.trim()}` : '';
    
    },
    iconSizeClass() {

      if (this.isSFSizes) {

        switch (this.size.trim()) {

          case 'xxs':
            return 'size-xxs';
          case 'xs':
            return 'size-xs';
          case 'sm':
            return 'size-sm';
          case 'md':
            return 'size-md';
          case 'lg':
            return 'size-lg';
          case 'xl':
            return 'size-xl';
          case 'xxl':
            return 'size-xxl';
          case 'xl3':
            return 'size-xl3';
          case 'xl4':
            return 'size-xl4';
          default:
            return 'size-lg';
        
        }
      
      } else {

        return '';
      
      }
    
    },
    iconCustomStyle() {

      return {
        '--icon-color': !this.isSFColors ? this.color : '',
        '--icon-size': !this.isSFSizes ? this.size : '',
      };
    
    },
    isSFIcons() {

      if (typeof this.icon === 'string') {

        return Object.keys(sfIcons).includes(this.icon.trim());
      
      } else {

        return null;

      }
    
    },
    iconViewBox() {

      return this.isSFIcons
        ? sfIcons[this.icon].viewBox || this.viewBox
        : this.viewBox;
    
    },
    iconPaths() {

      if (this.isSFIcons) {

        return sfIcons[this.icon].paths;
      
      } else {

        return Array.isArray(this.icon) ? this.icon : [this.icon];
      
      }
    
    },
    fillPath() {

      return this.coverageNumber === 1
        ? 'var(--icon-color)'
        : this.fillPathUrl(this.gradientUid);
    
    },
  },
  methods: {
    fillPathUrl(uid) {

      return `url(#linearGradient-${uid})`;
    
    },
  },
};
</script>
<style lang="scss">
// Icon sizes
$sizes: (
  "xxs": 1rem,
  "xs": 1.4rem,
  "sm": 1.625rem,
  "md": 1.8rem,
  "lg": 2rem,
  "xl": 2.2rem,
  "xxl": 2.5rem,
  "xl3": 2.8rem,
  "xl4": 3.25rem
);

.sf-icon {
  position: var(--icon-position, relative);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--icon-width, var(--icon-size, #{map.get($sizes, "sm")}));
  height: var(--icon-height, var(--icon-size, #{map.get($sizes, "sm")}));
  background: transparent;
  fill: var(--icon-color, #{map.get($icon-colors, "black")});

  svg,
  img {
    width: inherit;
    height: inherit;
  }

  &__badge {
    position: absolute;
    top: var(--icon-badge-top, 0);
    right: var(--icon-badge-right, 0);
  }

  @each $palette, $color in $icon-colors {
    &.color-#{$palette} {
      --icon-color: #{$color};
    }
  }

  @each $profile, $size in $sizes {
    &.size-#{$profile} {
      --icon-size: #{$size};
    }
  }
}
</style>
